import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Link from 'flarum/common/components/Link';
import Select from 'flarum/common/components/Select';
import { t, tt, route, find, syncLocale } from '../i18n';

// Tabs bovenaan het overzicht; '' = alle artiesten. Wie meerdere rollen heeft, staat onder elke tab.
const TABS = ['', 'dj', 'producer', 'project'];

const SORTS = ['name', 'clubs', 'popular'];

/**
 * /artiesten — alle DJ's, producers en live-acts, met zoeken en filters.
 */
export default class ArtistsPage extends Page {
  oninit(vnode) {
    super.oninit(vnode);
    syncLocale();

    this.loading = true;
    this.artists = [];
    this.query = '';
    this.role = m.route.param('rol') || '';
    this.club = '';
    this.sort = 'clubs';

    app.history.push('lc.artists', tt('artists.title'));
    app.setTitle(tt('artists.title'));
    app.setTitleCount(0);

    const preloaded = app.store.all('lc-artists');
    if (preloaded.length > 20) {
      this.artists = preloaded;
      this.loading = false;
    }

    find('lc-artists').then((artists) => {
      this.artists = artists;
      this.loading = false;
      m.redraw();
    });
    if (app.store.all('lc-clubs').length < 20) find('lc-clubs').then(() => m.redraw());
  }

  onbeforeupdate(vnode) {
    super.onbeforeupdate(vnode);
    this.role = m.route.param('rol') || '';
  }

  view() {
    const list = this.sorted(this.filter(this.artists));
    const counts = this.tabCounts();
    const clubs = this.clubOptions();

    return (
      <div className="LcClubsPage LcArtistsPage">
        <section className="LcHero LcHero--artists">
          <div className="container LcHero-inner">
            <div className="LcHero-text">
              <p className="LcEyebrow">The Real Clubhistory</p>
              <h1 className="LcHero-title">{t('artists_page.hero_title', { span: <span /> })}</h1>
              <p className="LcHero-lead">{t('artists.intro', { count: this.artists.length })}</p>
              <div className="LcHero-search">
                <i className="fas fa-search" aria-hidden="true" />
                <input
                  className="FormControl"
                  type="search"
                  placeholder={tt('artists.search_placeholder')}
                  value={this.query}
                  oninput={(e) => (this.query = e.target.value)}
                  aria-label={tt('artists.search_placeholder')}
                />
              </div>
            </div>
          </div>
        </section>

        <section className="container LcArchive">
          <nav className="LcTabs" aria-label={tt('artists.tabs.label')}>
            {TABS.map((role) => (
              <Link
                className={'LcTab' + (this.role === role ? ' is-active' : '')}
                href={role ? route('lc.artists', { rol: role }) : route('lc.artists')}
              >
                {t(`artists.tabs.${role || 'all'}`)}
                <span className="LcTab-count">{counts[role]}</span>
              </Link>
            ))}
          </nav>
          <div className="LcFilters">
            <Select value={this.club} options={{ '': tt('artists.all_clubs'), ...clubs }} onchange={(value) => (this.club = value)} />
            <Select value={this.sort} options={Object.fromEntries(SORTS.map((k) => [k, tt(`common.sort.${k}`)]))} onchange={(value) => (this.sort = value)} />
            <span className="LcFilters-count">{t('artists.count', { count: list.length })}</span>
          </div>

          {this.loading ? (
            <LoadingIndicator />
          ) : list.length ? (
            <ul className="LcGrid LcGrid--small">{list.map((a) => this.card(a))}</ul>
          ) : (
            <p className="LcEmpty">{t('artists.no_results')}</p>
          )}
        </section>
      </div>
    );
  }

  card(artist) {
    const names = (artist.clubSlugs() || []).map((s) => this.clubName(s)).filter(Boolean);
    return (
      <li key={artist.slug()} className={'LcCard' + (artist.hasStory() ? ' LcCard--featured' : '')}>
        <Link href={route('lc.artist', { slug: artist.slug() })} className="LcCard-link">
          <h3 className="LcCard-name">{artist.name()}</h3>
          <span className="LcCard-years LcCard-role">
            {[artist.roleList().map((r) => tt(`artist.role.${r}`)).join(' · '), artist.activeYears()].filter(Boolean).join(' · ')}
          </span>
          {names.length ? (
            <span className="LcCard-place">
              <i className="fas fa-compact-disc" aria-hidden="true" /> {names.slice(0, 3).join(', ')}
              {names.length > 3 ? ` +${names.length - 3}` : ''}
            </span>
          ) : null}
          {artist.forumMentions() ? (
            <span className="LcCard-meta">
              <i className="far fa-comments" aria-hidden="true" /> {t('artists.mentions', { count: artist.forumMentions() })}
            </span>
          ) : null}
        </Link>
      </li>
    );
  }

  clubName(slug) {
    const club = app.store.all('lc-clubs').find((c) => c.slug() === slug);
    return club ? club.name() : null;
  }

  /** Clubs die bij minstens één artiest voorkomen. */
  clubOptions() {
    const slugs = new Set();
    this.artists.forEach((a) => (a.clubSlugs() || []).forEach((s) => slugs.add(s)));
    return Object.fromEntries(
      Array.from(slugs)
        .map((s) => [s, this.clubName(s) || s])
        .sort((a, b) => a[1].localeCompare(b[1], 'nl'))
    );
  }

  filter(artists) {
    const q = this.query.trim().toLowerCase();
    return artists.filter((a) => {
      if (q && !a.searchText().includes(q)) return false;
      if (this.role && !a.roleList().includes(this.role)) return false;
      if (this.club && !(a.clubSlugs() || []).includes(this.club)) return false;
      return true;
    });
  }

  /** Aantal artiesten per tab (met de zoekterm en clubfilter toegepast). */
  tabCounts() {
    const role = this.role;
    const counts = {};
    TABS.forEach((r) => {
      this.role = r;
      counts[r] = this.filter(this.artists).length;
    });
    this.role = role;
    return counts;
  }

  sorted(artists) {
    const list = artists.slice();
    const byName = (a, b) => a.name().localeCompare(b.name(), 'nl');
    if (this.sort === 'name') list.sort(byName);
    // 'meeste clubs': bij gelijke stand eerst wie een volledig verhaal heeft, dan de meest besproken
    if (this.sort === 'clubs')
      list.sort(
        (a, b) =>
          (b.clubSlugs() || []).length - (a.clubSlugs() || []).length ||
          (b.hasStory() ? 1 : 0) - (a.hasStory() ? 1 : 0) ||
          (b.forumMentions() || 0) - (a.forumMentions() || 0) ||
          byName(a, b)
      );
    if (this.sort === 'popular') list.sort((a, b) => (b.forumMentions() || 0) - (a.forumMentions() || 0) || byName(a, b));
    return list;
  }
}
